import { useOrgMembers, type UseOrgMembersOptions } from './useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';

export interface UseDepartmentMembersOptions extends Omit<UseOrgMembersOptions, 'departmentId'> {
  /**
   * Optional department ID to override the default managed department.
   * If provided, fetches members from this department instead.
   * Useful when user manages multiple departments.
   */
  departmentId?: string;
}

/**
 * Fetch members of a managed department
 *
 * This hook filters organization members to show only those in the specified
 * department. If no departmentId is provided, it defaults to the first
 * department the current user manages.
 *
 * @param options - Optional filters including departmentId override
 * @returns Query result containing department members
 *
 * @example
 * ```tsx
 * // Use default (first managed) department
 * function DepartmentMembersList() {
 *   const { data: members, isLoading } = useDepartmentMembers();
 *   return <List items={members} />;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Use specific department (for multi-department managers)
 * function DepartmentMembersList({ selectedDeptId }: { selectedDeptId: string }) {
 *   const { data: members } = useDepartmentMembers({ departmentId: selectedDeptId });
 *   return <List items={members} />;
 * }
 * ```
 */
export function useDepartmentMembers(options?: UseDepartmentMembersOptions) {
  const { authContext } = usePermissions();
  const defaultDepartmentId = authContext?.managedDeptIds[0];

  // Use provided departmentId or fall back to default managed department
  const departmentId = options?.departmentId || defaultDepartmentId;

  return useOrgMembers({
    ...options,
    departmentId
  });
}
